import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener, ElementRef, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { WaterMappingViewService, WaterMappingRow, WaterMappingDetailRequest, FilterColumn } from '../../services/water-mapping-view.service';
import { TableColumn } from '../../services/interfaces/water-mapping-view.interfaces';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-water-mapping-view',
  standalone: false,
  templateUrl: './water-mapping-view.component.html',
  styleUrls: ['./water-mapping-view.component.scss']
})
export class WaterMappingViewComponent implements OnInit, OnDestroy {
  private readonly blankFilterToken = 'NULL';

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  spinnerBoolean: boolean = false;
  data: WaterMappingRow[] = [];
  selectedRows: WaterMappingRow[] = [];
  showCancel: boolean = false;
  dropdownLoading: boolean = false;
  loading: boolean = false;
  hoveredRowIndex: number | null = null;
  dropdownsLoaded: boolean = false;

  // Pagination tracking
  nextRowNumber: number | null = 0;
  hasMore: boolean = true;

  // Global dropdown options
  regionOptions: string[] = [];
  developmentAreaOptions: string[] = [];
  subDevelopmentAreaOptions: string[] = [];
  facilityNameOptions: string[] = [];     // Changed from feFacilityNameOptions
  interConnection1Options: string[] = []; // Changed from interconnection1Options
  interConnection2Options: string[] = []; // Changed from interconnection2Options
  interConnection3Options: string[] = []; // Changed from interconnection3Options
  interConnection4Options: string[] = []; // Changed from interconnection4Options
  disposalOutletNameOptions: string[] = [];

  // Per-row filtered dropdown options (for cascading)
  regionOptionsMap: { [rowId: string]: string[] } = {};
  developmentAreaOptionsMap: { [rowId: string]: string[] } = {};
  subDevelopmentAreaOptionsMap: { [rowId: string]: string[] } = {};
  facilityNameOptionsMap: { [rowId: string]: string[] } = {};    // Changed from feFacilityNameOptionsMap
  interConnection1OptionsMap: { [rowId: string]: string[] } = {};
  interConnection2OptionsMap: { [rowId: string]: string[] } = {};
  interConnection3OptionsMap: { [rowId: string]: string[] } = {};
  interConnection4OptionsMap: { [rowId: string]: string[] } = {};
  disposalOutletNameOptionsMap: { [rowId: string]: string[] } = {};

  // Filter-related properties
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  currentFilters: { [key: string]: string | null } = {
    region: null,
    developmentArea: null,    // Changed from feDevArea
    facilityName: null,       // Changed from feFacilityName
    interConnection1: null,   // Changed from interconnection1
    interConnection2: null,   // Changed from interconnection2
    interConnection3: null,   // Changed from interconnection3
    interConnection4: null,   // Changed from interconnection4
    disposalOutletName: null,
  };

  public dialogMessage: string = '';
  private destroy$ = new Subject<void>();

  columns: TableColumn[] = [
    { field: 'region', header: 'Region', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'developmentArea', header: 'FE DEV Area', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'facilityName', header: 'FE Facility Name', required: true, filterable: true, filterType: 'multi-select' },
    { field: 'interConnection1', header: 'Interconnection 1', required: false, filterable: true, filterType: 'multi-select' },
    { field: 'interConnection2', header: 'Interconnection 2', required: false, filterable: true, filterType: 'multi-select' },
    { field: 'interConnection3', header: 'Interconnection 3', required: false, filterable: true, filterType: 'multi-select' },
    { field: 'interConnection4', header: 'Interconnection 4', required: false, filterable: true, filterType: 'multi-select' },
    { field: 'disposalOutletName', header: 'Disposal Outlet Name', required: true, filterable: true, filterType: 'multi-select' }
  ];

  constructor(
    private toastr: ToastrService,
    private waterMappingService: WaterMappingViewService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      await this.loadWaterMappingData();
      await this.loadFilterOptionsInit();
      await this.loadInitialDropdowns();
    } catch (error) {
      this.toastr.error('Error during component initialization', 'Error');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadInitialDropdowns(): Promise<void> {
    try {
      this.dropdownLoading = true;
      const dropdownData = await this.waterMappingService.getAllDropdownOptions();
      
      this.regionOptions = dropdownData.regions || [];
      this.developmentAreaOptions = dropdownData.developmentAreas || [];
      this.subDevelopmentAreaOptions = dropdownData.subDevelopmentAreas || [];
      this.facilityNameOptions = dropdownData.facilityNames || [];
      this.interConnection1Options = dropdownData.interConnection1Options || [];
      this.interConnection2Options = dropdownData.interConnection2Options || [];
      this.interConnection3Options = dropdownData.interConnection3Options || [];
      this.interConnection4Options = dropdownData.interConnection4Options || [];
      this.disposalOutletNameOptions = dropdownData.disposalOutletNames || [];
      this.dropdownsLoaded = true;

      this.data.forEach(row => {
        if (!this.regionOptionsMap[row._rowId]?.length) this.regionOptionsMap[row._rowId] = [...this.regionOptions];
        if (!this.developmentAreaOptionsMap[row._rowId]?.length) this.developmentAreaOptionsMap[row._rowId] = [...this.developmentAreaOptions];
        if (!this.subDevelopmentAreaOptionsMap[row._rowId]?.length) this.subDevelopmentAreaOptionsMap[row._rowId] = [...this.subDevelopmentAreaOptions];
        if (!this.facilityNameOptionsMap[row._rowId]?.length) this.facilityNameOptionsMap[row._rowId] = [...this.facilityNameOptions];
        if (!this.interConnection1OptionsMap[row._rowId]?.length) this.interConnection1OptionsMap[row._rowId] = [...this.interConnection1Options];
        if (!this.interConnection2OptionsMap[row._rowId]?.length) this.interConnection2OptionsMap[row._rowId] = [...this.interConnection2Options];
        if (!this.interConnection3OptionsMap[row._rowId]?.length) this.interConnection3OptionsMap[row._rowId] = [...this.interConnection3Options];
        if (!this.interConnection4OptionsMap[row._rowId]?.length) this.interConnection4OptionsMap[row._rowId] = [...this.interConnection4Options];
        if (!this.disposalOutletNameOptionsMap[row._rowId]?.length) this.disposalOutletNameOptionsMap[row._rowId] = [...this.disposalOutletNameOptions];
      });


    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
      
      // Initialize empty dropdown options when API fails
      this.regionOptions = [];
      this.developmentAreaOptions = [];
      this.subDevelopmentAreaOptions = [];
      this.facilityNameOptions = [];
      this.interConnection1Options = [];
      this.interConnection2Options = [];
      this.interConnection3Options = [];
      this.interConnection4Options = [];
      this.disposalOutletNameOptions = [];
      this.dropdownsLoaded = false;
    } finally {
      this.dropdownLoading = false;
    }
  }

  async ensureDropdownsLoaded(): Promise<void> {
    if (this.dropdownsLoaded || this.dropdownLoading) {
      return;
    }

    await this.loadInitialDropdowns();
  }

  async onDropdownFocus(row: WaterMappingRow): Promise<void> {
    await this.ensureDropdownsLoaded();

    if (!this.regionOptionsMap[row._rowId]?.length) this.regionOptionsMap[row._rowId] = [...this.regionOptions];
    if (!this.developmentAreaOptionsMap[row._rowId]?.length) this.developmentAreaOptionsMap[row._rowId] = [...this.developmentAreaOptions];
    if (!this.subDevelopmentAreaOptionsMap[row._rowId]?.length) this.subDevelopmentAreaOptionsMap[row._rowId] = [...this.subDevelopmentAreaOptions];
    if (!this.facilityNameOptionsMap[row._rowId]?.length) this.facilityNameOptionsMap[row._rowId] = [...this.facilityNameOptions];
    if (!this.interConnection1OptionsMap[row._rowId]?.length) this.interConnection1OptionsMap[row._rowId] = [...this.interConnection1Options];
    if (!this.interConnection2OptionsMap[row._rowId]?.length) this.interConnection2OptionsMap[row._rowId] = [...this.interConnection2Options];
    if (!this.interConnection3OptionsMap[row._rowId]?.length) this.interConnection3OptionsMap[row._rowId] = [...this.interConnection3Options];
    if (!this.interConnection4OptionsMap[row._rowId]?.length) this.interConnection4OptionsMap[row._rowId] = [...this.interConnection4Options];
    if (!this.disposalOutletNameOptionsMap[row._rowId]?.length) this.disposalOutletNameOptionsMap[row._rowId] = [...this.disposalOutletNameOptions];

    await this.refreshRowDropdownOptions(row, this.shouldUseNullDropdownPayload(row));
  }

  async onInterconnectionDropdownOpen(row: WaterMappingRow): Promise<void> {
    await this.ensureDropdownsLoaded();
    await this.refreshRowDropdownOptions(row, this.shouldUseNullDropdownPayload(row));
  }

  private shouldUseNullDropdownPayload(row: WaterMappingRow): boolean {
    return !row.region &&
      !row.developmentArea &&
      !row.facilityName &&
      !row.interConnection1 &&
      !row.interConnection2 &&
      !row.interConnection3 &&
      !row.interConnection4 &&
      !row.disposalOutletName;
  }

  async loadFilterOptionsInit(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const filterData = await this.waterMappingService.getFilterOptionsForUI({
        region: null,
        developmentArea: null,
        facilityName: null,
        interConnection1: null,
        interConnection2: null,
        interConnection3: null,
        interConnection4: null,
        disposalOutletName: null
      });

      this.filterColumnsData = this.normalizeFilterColumns(filterData);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filter options';
      this.toastr.error(errorMessage, 'Error');
      
      // Initialize empty filter data if API fails
      this.filterColumnsData = {
        region: [],
        developmentArea: [],
        facilityName: [],
        interConnection1: [],
        interConnection2: [],
        interConnection3: [],
        interConnection4: [],
        disposalOutletName: []
      };
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async reloadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const filterData = await this.waterMappingService.getFilterOptionsForUI({
        region: this.currentFilters['region'] || null,
        developmentArea: this.currentFilters['developmentArea'] || null,
        facilityName: this.currentFilters['facilityName'] || null,
        interConnection1: this.currentFilters['interConnection1'] || null,
        interConnection2: this.currentFilters['interConnection2'] || null,
        interConnection3: this.currentFilters['interConnection3'] || null,
        interConnection4: this.currentFilters['interConnection4'] || null,
        disposalOutletName: this.currentFilters['disposalOutletName'] || null
      });

      this.filterColumnsData = this.normalizeFilterColumns(filterData);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reload filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async loadWaterMappingData(filters?: Partial<WaterMappingDetailRequest>): Promise<void> {
    try {
      this.spinnerBoolean = true;
      
      const filterPayload: WaterMappingDetailRequest = {
        region: this.currentFilters['region'] || null,
        developmentArea: this.currentFilters['developmentArea'] || null,
        facilityName: this.currentFilters['facilityName'] || null,
        interConnection1: this.currentFilters['interConnection1'] || null,
        interConnection2: this.currentFilters['interConnection2'] || null,
        interConnection3: this.currentFilters['interConnection3'] || null,
        interConnection4: this.currentFilters['interConnection4'] || null,
        disposalOutletName: this.currentFilters['disposalOutletName'] || null,
        lastRowNumber: filters?.lastRowNumber ?? 0,
        ...filters
      };

      const response = await this.waterMappingService.getWaterMappingData(filterPayload);
      
      this.nextRowNumber = response.nextRowNumber ?? null;
      this.hasMore = this.nextRowNumber !== null && this.nextRowNumber > 0;

      if (response.data && response.data.length > 0) {
        let newRows = response.data.map((item): WaterMappingRow => ({
          _rowId: this.generateRowId(),
          region: item.region ?? '',
          developmentArea: item.developmentArea ?? '',
          facilityName: item.facilityName ?? '',
          interConnection1: item.interConnection1 ?? '',
          interConnection2: item.interConnection2 ?? '',
          interConnection3: item.interConnection3 ?? '',
          interConnection4: item.interConnection4 ?? '',
          disposalOutletName: item.disposalOutletName ?? '',
          isNew: false,
          _rowEdited: false,
          original: {}
        }));

        for (const row of newRows) {
          row.original = { ...row };
          this.regionOptionsMap[row._rowId] = [...this.regionOptions];
          this.developmentAreaOptionsMap[row._rowId] = [...this.developmentAreaOptions];
          this.subDevelopmentAreaOptionsMap[row._rowId] = [...this.subDevelopmentAreaOptions];
          this.facilityNameOptionsMap[row._rowId] = [...this.facilityNameOptions];
          this.interConnection1OptionsMap[row._rowId] = [...this.interConnection1Options];
          this.interConnection2OptionsMap[row._rowId] = [...this.interConnection2Options];
          this.interConnection3OptionsMap[row._rowId] = [...this.interConnection3Options];
          this.interConnection4OptionsMap[row._rowId] = [...this.interConnection4Options];
          this.disposalOutletNameOptionsMap[row._rowId] = [...this.disposalOutletNameOptions];
        }

        if (filterPayload.lastRowNumber === 0) {
          this.data = newRows;
        } else {
          this.mergeRows(newRows);
        }

      } else {
        if (filterPayload.lastRowNumber === 0) {
          this.data = [];
          if (!this.hasAnyFilterApplied()) {
            this.toastr.info('No existing water mapping data found', 'Info');
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load water mapping data';
      this.toastr.error(errorMessage, 'Error');
      this.hasMore = false;
    } finally {
      this.spinnerBoolean = false;
      this.loading = false;
    }
  }

  private mergeRows(newRows: WaterMappingRow[]): void {
    const keyOf = (r: WaterMappingRow) =>
      `${r.region}||${r.developmentArea}||${r.facilityName}`;

    const existingKeys = new Set(this.data.map(keyOf));
    const toAdd = newRows.filter((r) => !existingKeys.has(keyOf(r)));
    this.data = [...this.data, ...toAdd];
  }

  private isBlankFilterValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    const normalized = String(value).trim();
    return normalized === '' || normalized.toLowerCase() === 'null';
  }

  private normalizeFilterValue(value: unknown): string {
    if (this.isBlankFilterValue(value)) {
      return this.blankFilterToken;
    }

    return String(value).trim();
  }

  private normalizeFilterOptions(values: unknown[] | undefined): string[] {
    if (!Array.isArray(values)) {
      return [];
    }

    const normalizedValues = Array.from(
      new Set(values.map((value) => this.normalizeFilterValue(value)))
    );

    const blankIndex = normalizedValues.indexOf(this.blankFilterToken);
    if (blankIndex > 0) {
      normalizedValues.splice(blankIndex, 1);
      normalizedValues.unshift(this.blankFilterToken);
    }

    return normalizedValues;
  }

  private normalizeFilterColumns(filterData: { [key: string]: string[] }): { [key: string]: string[] } {
    const fieldsWithBlankOption = new Set([
      'interConnection1',
      'interConnection2',
      'interConnection3',
      'interConnection4',
    ]);

    return Object.fromEntries(
      Object.entries(filterData).map(([field, values]) => [
        field,
        fieldsWithBlankOption.has(field) ? this.normalizeFilterOptions(values) : values || [],
      ])
    );
  }

  loadFirstPage(): void {
    this.data = [];
    this.nextRowNumber = 0;
    this.hasMore = true;
    this.loadWaterMappingData({ lastRowNumber: 0 });
  }

  onScrolledIndexChange(index: number): void {
    const buffer = 10;
    if (
      this.hasMore &&
      !this.loading &&
      !this.spinnerBoolean &&
      index >= Math.max(0, this.data.length - buffer)
    ) {
      this.loading = true;
      this.loadWaterMappingData({ lastRowNumber: this.nextRowNumber ?? 0 });
    }
  }

  // Cascading dropdown handlers
  async onRegionChange(row: WaterMappingRow, selectedRegion: string): Promise<void> {
    try {
      this.markAsEdited(row);
      await this.refreshRowDropdownOptions(row);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async onDevelopmentAreaChange(row: WaterMappingRow, selectedArea: string): Promise<void> {
    try {
      this.markAsEdited(row);
      await this.refreshRowDropdownOptions(row);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async onFacilityNameChange(row: WaterMappingRow, selectedFacility: string): Promise<void> {
    this.markAsEdited(row);
    await this.refreshRowDropdownOptions(row);
  }

  // Handle Interconnection dropdown changes
  onInterConnectionChange(row: WaterMappingRow, field: 'interConnection1' | 'interConnection2' | 'interConnection3' | 'interConnection4', value: string): void {
    this.markAsEdited(row);
    this.refreshRowDropdownOptions(row);
  }

  // Handle Disposal Outlet dropdown changes
  onDisposalOutletChange(row: WaterMappingRow, value: string): void {
    this.markAsEdited(row);
    this.refreshRowDropdownOptions(row);
  }

  private async refreshRowDropdownOptions(row: WaterMappingRow, useNullInputs: boolean = false): Promise<void> {
    try {
      this.dropdownLoading = true;
      const requestPayload = useNullInputs
        ? {
            region: null,
            developmentArea: null,
            facilityName: null,
            interConnection1: null,
            interConnection2: null,
            interConnection3: null,
            interConnection4: null,
            disposalOutletName: null,
          }
        : {
            region: row.region || null,
            developmentArea: row.developmentArea || null,
            facilityName: row.facilityName || null,
            interConnection1: row.interConnection1 || null,
            interConnection2: row.interConnection2 || null,
            interConnection3: row.interConnection3 || null,
            interConnection4: row.interConnection4 || null,
            disposalOutletName: row.disposalOutletName || null,
          };

      const response = await this.waterMappingService.getDropdownOptions({
        ...requestPayload,
      });

      this.regionOptionsMap[row._rowId] = response.regions || [];
      this.developmentAreaOptionsMap[row._rowId] = response.developmentAreas || [];
      this.subDevelopmentAreaOptionsMap[row._rowId] = response.subDevelopmentAreas || [];
      this.facilityNameOptionsMap[row._rowId] = response.facilityNames || [];
      this.interConnection1OptionsMap[row._rowId] = response.interConnection1Options || [];
      this.interConnection2OptionsMap[row._rowId] = response.interConnection2Options || [];
      this.interConnection3OptionsMap[row._rowId] = response.interConnection3Options || [];
      this.interConnection4OptionsMap[row._rowId] = response.interConnection4Options || [];
      this.disposalOutletNameOptionsMap[row._rowId] = response.disposalOutletNames || [];
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.dropdownLoading = false;
    }
  }

  getRegionOptionsForRow(row: WaterMappingRow): string[] {
    const options = (this.regionOptionsMap[row._rowId] !== undefined ? this.regionOptionsMap[row._rowId] : this.regionOptions);
    return this.withCurrentValue(
      options,
      row.region
    );
  }

  getDevelopmentAreaOptionsForRow(row: WaterMappingRow): string[] {
    const options = (this.developmentAreaOptionsMap[row._rowId] !== undefined ? this.developmentAreaOptionsMap[row._rowId] : this.developmentAreaOptions);
    return this.withCurrentValue(
      options,
      row.developmentArea
    );
  }

  getSubDevelopmentAreaOptionsForRow(row: WaterMappingRow): string[] {
    const options = (this.subDevelopmentAreaOptionsMap[row._rowId] !== undefined ? this.subDevelopmentAreaOptionsMap[row._rowId] : this.subDevelopmentAreaOptions);
    const devOptions = this.getDevelopmentAreaOptionsForRow(row);
    const selectedValue = (row.developmentArea || '').trim();

    if (selectedValue && !options.includes(selectedValue) && !devOptions.includes(selectedValue)) {
      return [selectedValue, ...options];
    }

    return options;
  }

  getFacilityNameOptionsForRow(row: WaterMappingRow): string[] {
    const options = (this.facilityNameOptionsMap[row._rowId] !== undefined ? this.facilityNameOptionsMap[row._rowId] : this.facilityNameOptions);
    return this.withCurrentValue(
      options,
      row.facilityName
    );
  }

  getDropdownOptionsForRow(row: WaterMappingRow, field: keyof WaterMappingRow): string[] {
    const currentValue = row[field] as unknown as string;

    const optionsMap: Partial<Record<keyof WaterMappingRow, string[]>> = {
      interConnection1: this.interConnection1OptionsMap[row._rowId] !== undefined ? this.interConnection1OptionsMap[row._rowId] : this.interConnection1Options,
      interConnection2: this.interConnection2OptionsMap[row._rowId] !== undefined ? this.interConnection2OptionsMap[row._rowId] : this.interConnection2Options,
      interConnection3: this.interConnection3OptionsMap[row._rowId] !== undefined ? this.interConnection3OptionsMap[row._rowId] : this.interConnection3Options,
      interConnection4: this.interConnection4OptionsMap[row._rowId] !== undefined ? this.interConnection4OptionsMap[row._rowId] : this.interConnection4Options,
      disposalOutletName: this.disposalOutletNameOptionsMap[row._rowId] !== undefined ? this.disposalOutletNameOptionsMap[row._rowId] : this.disposalOutletNameOptions,
    };

    return this.withCurrentValue(optionsMap[field] || [], currentValue);
  }

  private withCurrentValue(options: string[], currentValue: string): string[] {
    const sourceOptions = options || [];

    if (sourceOptions.length === 0) {
      return [];
    }

    if (!currentValue) {
      return sourceOptions;
    }

    const normalizedCurrent = String(currentValue).trim();
    if (!normalizedCurrent) {
      return sourceOptions;
    }

    const exists = sourceOptions.some(
      option => String(option).trim().toLowerCase() === normalizedCurrent.toLowerCase()
    );

    if (exists) {
      return sourceOptions;
    }

    return [normalizedCurrent, ...sourceOptions];
  }

  addRow(): void {
    const newRow: WaterMappingRow = { 
      _rowId: this.generateRowId(), 
      region: '', 
      developmentArea: '', 
      facilityName: '', 
      interConnection1: '', 
      interConnection2: '', 
      interConnection3: '', 
      interConnection4: '',
      disposalOutletName: '',
      isNew: true, 
      _rowEdited: false, 
      original: {} 
    };
    
    this.regionOptionsMap[newRow._rowId] = [...this.regionOptions];
    this.developmentAreaOptionsMap[newRow._rowId] = [...this.developmentAreaOptions];
    this.subDevelopmentAreaOptionsMap[newRow._rowId] = [...this.subDevelopmentAreaOptions];
    this.facilityNameOptionsMap[newRow._rowId] = [...this.facilityNameOptions];
    this.interConnection1OptionsMap[newRow._rowId] = [...this.interConnection1Options];
    this.interConnection2OptionsMap[newRow._rowId] = [...this.interConnection2Options];
    this.interConnection3OptionsMap[newRow._rowId] = [...this.interConnection3Options];
    this.interConnection4OptionsMap[newRow._rowId] = [...this.interConnection4Options];
    this.disposalOutletNameOptionsMap[newRow._rowId] = [...this.disposalOutletNameOptions];
    
    this.data = [newRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();
    
    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    });
    
    this.toastr.info('New water mapping row added. Click Cancel to discard or Save to keep changes.');

    void this.refreshRowDropdownOptions(newRow, true);
  }

  copyRow(index: number): void {
    const originalRow = this.data[index];
    if (!originalRow) return;

    const copiedRow: WaterMappingRow = {
      _rowId: this.generateRowId(),
      region: originalRow.region ?? '',
      developmentArea: originalRow.developmentArea ?? '',
      facilityName: originalRow.facilityName ?? '',
      interConnection1: '',
      interConnection2: '',
      interConnection3: '',
      interConnection4: '',
      disposalOutletName: originalRow.disposalOutletName ?? '',
      isNew: true,
      _rowEdited: false,
      original: {}
    };

    this.regionOptionsMap[copiedRow._rowId] = [...this.regionOptions];
    this.developmentAreaOptionsMap[copiedRow._rowId] = [...this.developmentAreaOptions];
    this.subDevelopmentAreaOptionsMap[copiedRow._rowId] = [...this.subDevelopmentAreaOptions];
    this.facilityNameOptionsMap[copiedRow._rowId] = [...this.facilityNameOptions];
    this.interConnection1OptionsMap[copiedRow._rowId] = [...this.interConnection1Options];
    this.interConnection2OptionsMap[copiedRow._rowId] = [...this.interConnection2Options];
    this.interConnection3OptionsMap[copiedRow._rowId] = [...this.interConnection3Options];
    this.interConnection4OptionsMap[copiedRow._rowId] = [...this.interConnection4Options];
    this.disposalOutletNameOptionsMap[copiedRow._rowId] = [...this.disposalOutletNameOptions];

    this.data = [copiedRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    }, 100);

    this.toastr.info('Row copied successfully. Click Cancel to discard or Save to keep changes.');
  }

  async handleSave(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      const required = this.columns.filter(col => col.required).map(col => col.field);
      const changedRows = this.data.filter(row => row.isNew || row._rowEdited);
      
      if (changedRows.length === 0) {
        this.toastr.info('No changes to save.');
        return;
      }

      const invalid = changedRows.filter(row => 
        required.some(field => {
          const value = row[field];
          return value === undefined || value === null || value === '';
        })
      );
      
      if (invalid.length) { 
        this.toastr.error('Please fill all required fields before saving.'); 
        return; 
      }

      this.spinnerBoolean = true;
      
      for (const row of changedRows) {
        const action: 'INSERT' | 'UPDATE' = row.isNew ? 'INSERT' : 'UPDATE';
        const hasOriginalSnapshot = !!row.original && Object.keys(row.original).length > 0;
        const originalRow = action === 'UPDATE'
          ? ((hasOriginalSnapshot ? row.original : row) as WaterMappingRow)
          : undefined;
        
        const response = await this.waterMappingService.saveWaterMapping(row, action, userName, originalRow);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          row.isNew = false;
          row._rowEdited = false;
          row.original = { ...row };
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      await this.loadWaterMappingData();

      if (successCount > 0) {
        this.toastr.success(`${successCount} water mapping row(s) saved successfully`, 'Success');
        this.showCancel = this.data.some(r => r.isNew || r._rowEdited);
      }

      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} water mapping row(s) failed to save`;
        
        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to save water mapping data', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) { 
      this.toastr.info('Please select rows to delete.'); 
      return; 
    }

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;

    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      this.spinnerBoolean = true;

      for (const row of this.selectedRows) {
        if (row.isNew) {
          const index = this.data.indexOf(row);
          if (index > -1) {
            this.data.splice(index, 1);
          }
          successCount++;
        } else {
          const response = await this.waterMappingService.deleteWaterMapping(row);
          const parsed = ErrorHandler.parseApiResult(response);

          if (parsed.success) {
            successCount++;
          } else {
            failureCount++;
            errorMessages.push(parsed.message || 'Unknown error occurred');
          }
        }
      }

      this.selectedRows.forEach(row => {
        delete this.regionOptionsMap[row._rowId];
        delete this.developmentAreaOptionsMap[row._rowId];
        delete this.subDevelopmentAreaOptionsMap[row._rowId];
        delete this.facilityNameOptionsMap[row._rowId];
        delete this.interConnection1OptionsMap[row._rowId];
        delete this.interConnection2OptionsMap[row._rowId];
        delete this.interConnection3OptionsMap[row._rowId];
        delete this.interConnection4OptionsMap[row._rowId];
        delete this.disposalOutletNameOptionsMap[row._rowId];
      });

      this.data = this.data.filter(row => !this.selectedRows.includes(row));
      this.selectedRows = [];

      await this.loadWaterMappingData();
      
      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }
      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to delete`;
        
        this.toastr.error(detailedErrorMsg, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to delete water mapping data', 'Error');
      this.toastr.error('Error deleting water mapping data', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  cancelRow(): void {
    this.data = this.data.filter((row) => !row.isNew);
    
    this.data.forEach(row => {
      if (row._rowEdited && row.original && Object.keys(row.original).length > 0) {
        Object.assign(row, row.original);
        row._rowEdited = false;
      }
    });
    
    this.showCancel = false;
    this.clearSelection();
    
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.viewport?.checkViewportSize();
    });
    
    this.toastr.info('Changes cancelled.');
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: WaterMappingRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (target.checked) { 
      if (!this.selectedRows.includes(row)) this.selectedRows.push(row); 
    } else { 
      const index = this.selectedRows.indexOf(row); 
      if (index > -1) this.selectedRows.splice(index, 1); 
    }
  }

  markAsEdited(row: WaterMappingRow): void {
    if (!row.original || Object.keys(row.original).length === 0) {
      row.original = { ...row };
    }
    if (!row.isNew) {
      row._rowEdited = true;
      this.showCancel = true;
    }
  }

  handleEdit(row: WaterMappingRow): void { 
    this.markAsEdited(row);
  }

  isIdentityFieldEditable(row: WaterMappingRow): boolean {
    return row.isNew;
  }

  // Filter methods
  async openFloatingFilter(event: MouseEvent, field: string): Promise<void> {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    if (this.getFilterType(field) === 'multi-select') {
      try {
        await this.reloadFilterOptions();
      } catch (error: any) {
        this.toastr.error(error.message || 'Failed to refresh filter options', 'Error');
      }
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 140 + window.scrollY,
      left: rect.left - 100 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFloatingFilter) {
      const target = event.target as HTMLElement;
      const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
      const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
        target.parentElement?.classList.contains('filter-icon');

      if (!clickedInside && !clickedOnFilterIcon) {
        this.closeFloatingFilter();
      }
    }
  }

  getFilterOptions(field: string): string[] {
    if (this.filterColumnsData[field]) {
      const options = this.filterColumnsData[field] || [];
      const search = (this.filterSearch[field] || '').toLowerCase();
      const filtered = options.filter(opt =>
        this.getFilterOptionLabel(String(opt)).toLowerCase().includes(search)
      );
      return filtered;
    }
    return [];
  }

  getFilterOptionLabel(option: string): string {
    return this.isBlankFilterValue(option) ? '(Blank)' : option;
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(v => v !== value);
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  getFilterType(field: string): 'multi-select' | 'date-range' | 'search' {
    const column = this.columns.find(col => col.field === field);
    return column?.filterType || 'multi-select';
  }

  async applyFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'multi-select') {
      const normalizedValues = (this.multiFilters[field] || [])
        .map((value) => this.normalizeFilterValue(value))
        .filter((value) => value !== '');

      if (normalizedValues.length === 1) {
        this.currentFilters[field] = normalizedValues[0];
      } else if (normalizedValues.length > 1) {
        this.currentFilters[field] = normalizedValues.join(',');
      } else {
        this.currentFilters[field] = null;
      }
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.reloadFilterOptions();
  }

  async clearFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field] = null;

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  hasAnyFilterApplied(): boolean {
    const hasCurrentFilter = Object.values(this.currentFilters).some(value => value !== null);
    return hasCurrentFilter;
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};

    Object.keys(this.currentFilters).forEach(key => {
      this.currentFilters[key] = null;
    });

    this.activeFilter = '';
    this.showFloatingFilter = false;

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  async exportToCSV(): Promise<void> {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row: WaterMappingRow) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((col) => {
        const value = row[col.field];
        exportRow[col.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((col) => col.header),
      'WaterMappingViewData.xlsx',
      'WaterMappingView'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.waterMappingService.exportAllWaterMappingData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  isFilterActive(field: string): boolean {
    const filterType = this.getFilterType(field);
    
    if (filterType === 'multi-select') {
      return (this.multiFilters[field] || []).length > 0;
    }
    
    return false;
  }

  generateRowId(): string { 
    return `row_${Date.now()}_${Math.random().toString(36).substr(2,9)}`; 
  }
}
